import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { licenciasService, TIPOS_DOCUMENTO } from '../services/licencias';
import toast from 'react-hot-toast';
import {
    ShieldCheck,
    Building2,
    Upload,
    FileText,
    Image as ImageIcon,
    History,
    List,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    HelpCircle,
    ExternalLink,
    X,
} from 'lucide-react';

const ALMACEN_STORAGE_KEY = 'licencias_almacen_seleccionado';

const ESTADO_CONFIG = {
    vigente:    { label: 'Vigente',      icon: CheckCircle2,  cls: 'bg-green-50 text-green-700 border-green-300' },
    por_vencer: { label: 'Vence este mes', icon: AlertTriangle, cls: 'bg-blue-50 text-blue-700 border-blue-300' },
    vencido:    { label: 'Vencido',      icon: XCircle,       cls: 'bg-red-50 text-red-700 border-red-300' },
    pendiente:  { label: 'Sin documento', icon: HelpCircle,   cls: 'bg-gray-100 text-gray-500 border-gray-300' },
};

const EstadoBadge = ({ estado }) => {
    const cfg = ESTADO_CONFIG[estado] || ESTADO_CONFIG.pendiente;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`}>
            <Icon className="h-3.5 w-3.5 mr-1" />
            {cfg.label}
        </span>
    );
};

const ModalHistorial = ({ almacen, tipoDocumento, tipoLabel, onCerrar }) => {
    const [items, setItems] = useState([]);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setItems(await licenciasService.historial(almacen, tipoDocumento));
            } catch {
                toast.error('Error al cargar el historial');
            } finally {
                setCargando(false);
            }
        })();
    }, [almacen, tipoDocumento]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between p-5 border-b">
                    <h2 className="text-base font-bold text-gray-900">
                        Historial — {tipoLabel} ({almacen})
                    </h2>
                    <button onClick={onCerrar} className="text-gray-400 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-5 overflow-y-auto space-y-3">
                    {cargando && <div className="text-center text-sm text-gray-400 py-6">Cargando...</div>}
                    {!cargando && items.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-6">Sin versiones anteriores.</p>
                    )}
                    {!cargando && items.map((doc) => (
                        <div key={doc.id} className={`border rounded-lg p-3 ${doc.activo ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200'}`}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-gray-700">
                                    {doc.fecha_renovacion
                                        ? `Renovación: ${new Date(doc.fecha_renovacion + 'T00:00:00').toLocaleDateString('es-PE')}`
                                        : 'Sin vencimiento'}
                                </span>
                                {doc.activo && <span className="text-[10px] px-1.5 py-0.5 bg-blue-600 text-white rounded-full font-bold">VIGENTE</span>}
                            </div>
                            <p className="text-xs text-gray-500">
                                Subido por {doc.subido_por || 'N/A'} — {new Date(doc.created_at).toLocaleString('es-PE')}
                            </p>
                            <a
                                href={doc.archivo_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-blue-600 hover:underline"
                            >
                                <ExternalLink className="h-3.5 w-3.5" /> Ver documento
                            </a>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const DocumentoCard = ({ almacen, tipoDocumento, tipoLabel, requiereFecha = true, documento, onActualizado }) => {
    const [subiendo, setSubiendo] = useState(false);
    const [mostrarForm, setMostrarForm] = useState(false);
    const [mostrarHistorial, setMostrarHistorial] = useState(false);
    const [fecha, setFecha] = useState(documento?.fecha_renovacion || '');
    const [archivo, setArchivo] = useState(null);
    const fileInputRef = useRef(null);

    const handleSubir = async () => {
        if (requiereFecha && !fecha) { toast.error('Selecciona la fecha de renovación'); return; }
        if (!archivo) { toast.error('Selecciona una imagen o PDF'); return; }
        setSubiendo(true);
        try {
            const actualizado = await licenciasService.subirDocumento(almacen, tipoDocumento, fecha, archivo);
            toast.success(`${tipoLabel} actualizado`);
            onActualizado(actualizado);
            setMostrarForm(false);
            setArchivo(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Error al subir el documento');
        } finally {
            setSubiendo(false);
        }
    };

    const esImagen = documento?.content_type?.startsWith('image/');

    return (
        <div className="border border-gray-200 rounded-xl p-5 bg-white flex flex-col">
            <div className="flex items-start justify-between mb-3 gap-2">
                <h3 className="font-semibold text-gray-800 text-sm">{tipoLabel}</h3>
                <EstadoBadge estado={documento?.estado || 'pendiente'} />
            </div>

            <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 border border-dashed border-gray-300 rounded-lg mb-3 min-h-[160px] p-3">
                {documento ? (
                    <>
                        {esImagen ? (
                            <a href={documento.archivo_url} target="_blank" rel="noreferrer" className="block">
                                <img
                                    src={documento.archivo_url}
                                    alt={tipoLabel}
                                    className="max-h-32 rounded-lg object-contain shadow-sm"
                                />
                            </a>
                        ) : (
                            <a
                                href={documento.archivo_url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex flex-col items-center text-red-600 hover:text-red-700"
                            >
                                <FileText className="h-12 w-12 mb-1" />
                                <span className="text-xs font-semibold">Ver PDF</span>
                            </a>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                            {documento.fecha_renovacion ? (
                                <>Vence: <span className="font-semibold">{new Date(documento.fecha_renovacion + 'T00:00:00').toLocaleDateString('es-PE')}</span></>
                            ) : (
                                <span className="font-semibold text-gray-400">Sin vencimiento</span>
                            )}
                        </p>
                    </>
                ) : (
                    <div className="flex flex-col items-center text-gray-400">
                        <ImageIcon className="h-10 w-10 mb-1 opacity-50" />
                        <span className="text-xs">Sin documento cargado</span>
                    </div>
                )}
            </div>

            {!mostrarForm ? (
                <div className="flex gap-2">
                    <button
                        onClick={() => setMostrarForm(true)}
                        className="flex-1 flex items-center justify-center px-3 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors"
                    >
                        <Upload className="h-3.5 w-3.5 mr-1.5" />
                        {documento ? 'Actualizar documento' : 'Adjuntar foto'}
                    </button>
                    <button
                        onClick={() => setMostrarHistorial(true)}
                        className="px-3 py-2 border border-gray-300 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                        title="Ver historial de versiones"
                    >
                        <History className="h-3.5 w-3.5" />
                    </button>
                </div>
            ) : (
                <div className="space-y-2 border-t border-gray-100 pt-3">
                    {requiereFecha && (
                        <>
                            <label className="block text-xs font-semibold text-gray-600">Fecha de renovación</label>
                            <input
                                type="date"
                                value={fecha}
                                onChange={(e) => setFecha(e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                        </>
                    )}
                    <label className="block text-xs font-semibold text-gray-600">Archivo (imagen o PDF)</label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf,application/pdf"
                        onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                        className="w-full text-xs"
                    />
                    <div className="flex gap-2 pt-1">
                        <button
                            onClick={handleSubir}
                            disabled={subiendo}
                            className="flex-1 px-3 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-60 transition-colors"
                        >
                            {subiendo ? 'Subiendo...' : 'Guardar'}
                        </button>
                        <button
                            onClick={() => { setMostrarForm(false); setArchivo(null); }}
                            className="px-3 py-2 border border-gray-300 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {mostrarHistorial && (
                <ModalHistorial
                    almacen={almacen}
                    tipoDocumento={tipoDocumento}
                    tipoLabel={tipoLabel}
                    onCerrar={() => setMostrarHistorial(false)}
                />
            )}
        </div>
    );
};

const LicenciasITSEPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [almacenes, setAlmacenes] = useState([]);
    const [documentos, setDocumentos] = useState(null);
    const [cargando, setCargando] = useState(false);

    const almacenParam = searchParams.get('almacen');
    const almacen = almacenParam || (typeof window !== 'undefined' ? localStorage.getItem(ALMACEN_STORAGE_KEY) : '') || '';

    const setAlmacen = (nuevo) => {
        const next = new URLSearchParams(searchParams);
        if (nuevo) {
            next.set('almacen', nuevo);
            try { localStorage.setItem(ALMACEN_STORAGE_KEY, nuevo); } catch {}
        } else {
            next.delete('almacen');
        }
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        (async () => {
            try {
                setAlmacenes(await licenciasService.listarAlmacenes());
            } catch {
                toast.error('No se pudo cargar la lista de almacenes');
            }
        })();
    }, []);

    const cargar = async () => {
        if (!almacen) { setDocumentos(null); return; }
        setCargando(true);
        try {
            setDocumentos(await licenciasService.obtenerPorAlmacen(almacen));
        } catch {
            toast.error('Error al cargar los documentos');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); }, [almacen]);

    const handleActualizado = (tipoDocumento, doc) => {
        setDocumentos((prev) => ({ ...(prev || {}), [tipoDocumento]: doc }));
    };

    return (
        <Layout>
            <div className="p-6">
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <ShieldCheck className="h-7 w-7 text-teal-600" />
                            Licencias e ITSE
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Licencia de Funcionamiento y Certificado ITSE por almacén.
                        </p>
                    </div>

                    <button
                        onClick={() => navigate('/gestion-documental/licencias-itse/resumen')}
                        className="flex items-center px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-sm shadow-sm"
                    >
                        <List className="h-4 w-4 mr-2" />
                        Ver Todo
                    </button>
                </div>

                <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2 text-gray-700">
                            <Building2 className="h-5 w-5 text-teal-600" />
                            <span className="text-sm font-semibold">Almacén:</span>
                        </div>
                        <select
                            value={almacen}
                            onChange={(e) => setAlmacen(e.target.value)}
                            className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                            <option value="">— Selecciona un almacén —</option>
                            {almacenes.map((a) => (
                                <option key={a} value={a}>{a}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {!almacen && (
                    <div className="text-center py-14 text-gray-400 bg-white rounded-xl shadow-sm border border-gray-200">
                        <Building2 className="h-14 w-14 mx-auto mb-3 opacity-20" />
                        <p className="text-sm font-medium">Selecciona un almacén para ver sus documentos.</p>
                    </div>
                )}

                {almacen && cargando && (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                    </div>
                )}

                {almacen && !cargando && documentos && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {TIPOS_DOCUMENTO.map((td) => (
                            <DocumentoCard
                                key={td.value}
                                almacen={almacen}
                                tipoDocumento={td.value}
                                tipoLabel={td.label}
                                requiereFecha={td.requiereFecha}
                                documento={documentos[td.value]}
                                onActualizado={(doc) => handleActualizado(td.value, doc)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default LicenciasITSEPage;
